// Per-copy configuration dialog for assistant messages.
// Portal-rendered modal that lets the user pick scope, reference exchange,
// and text format for a single copy operation.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'sonner';
import type { SourceDoc } from '../types';
import {
  buildCopy,
  findTables,
  type CopyChoices,
  type CopyRefFormat,
  type CopyScope,
  type CopyTextFormat,
} from '../lib/copyFormat';

interface CopySpecialDialogProps {
  body: string;
  sources?: SourceDoc[];
  initialScope: CopyScope;
  onClose: () => void;
}

const FORMAT_PILLS: { value: CopyTextFormat; label: string; enabledFor: 'any' | 'table' }[] = [
  { value: 'markdown', label: 'Markdown', enabledFor: 'any' },
  { value: 'plain', label: 'Plain text', enabledFor: 'any' },
  { value: 'latex', label: 'LaTeX', enabledFor: 'any' },
  { value: 'tsv', label: 'TSV', enabledFor: 'table' },
];

const REF_FORMAT_OPTIONS: { value: CopyRefFormat; label: string; hint: string }[] = [
  { value: 'wiki', label: 'Wiki-link', hint: 'Keep [[cite_key]] as-is' },
  { value: 'malpedia', label: 'Malpedia entry link', hint: '[1](/sources/cite_key)' },
  { value: 'url', label: 'Blog URL', hint: '[1](https://blog…) (falls back to /sources/…)' },
  { value: 'both', label: 'Blog URL + malpedia link', hint: '[1](https://blog…) ([cite_key](/sources/cite_key))' },
  { value: 'numbered-plain', label: 'Numbered only', hint: 'Just [1] — best with plain text' },
  { value: 'strip', label: 'Strip', hint: 'Remove citations entirely' },
];

const PREVIEW_MAX = 600;

function snippetFor(table: string): string {
  // First non-separator row text, trimmed.
  const lines = table.split('\n');
  const first = lines[0] || '';
  const cells = first
    .split('|')
    .map((c) => c.trim())
    .filter(Boolean);
  return cells.slice(0, 3).join(' · ').slice(0, 60);
}

function scopeKey(s: CopyScope): string {
  switch (s.kind) {
    case 'full':
      return 'full';
    case 'table':
      return `table-${s.index}`;
    case 'sources':
      return 'sources';
    case 'selection':
      return 'selection';
  }
}

export default function CopySpecialDialog({
  body,
  sources,
  initialScope,
  onClose,
}: CopySpecialDialogProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [choices, setChoices] = useState<CopyChoices>(() => ({
    scope: initialScope,
    refFormat: 'both',
    format: 'markdown',
    includeSourceTable: false,
  }));

  const tables = useMemo(() => findTables(body), [body]);
  const hasSources = !!sources && sources.length > 0;
  const selectionText =
    initialScope.kind === 'selection' ? initialScope.text : '';
  const hasSelection = selectionText.length > 0;

  // Close on Escape.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Close on overlay click.
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === overlayRef.current) onClose();
    },
    [onClose],
  );

  const update = useCallback((partial: Partial<CopyChoices>) => {
    setChoices((prev) => ({ ...prev, ...partial }));
  }, []);

  const setScope = useCallback(
    (s: CopyScope) => {
      update({ scope: s });
    },
    [update],
  );

  // If the user picks TSV then switches scope away from a table, snap back.
  useEffect(() => {
    if (choices.format === 'tsv' && choices.scope.kind !== 'table') {
      setChoices((prev) => ({ ...prev, format: 'markdown' }));
    }
  }, [choices.scope.kind, choices.format]);

  const preview = useMemo(() => {
    const out = buildCopy(body, sources, choices);
    return out.length > PREVIEW_MAX ? `${out.slice(0, PREVIEW_MAX)}\n…` : out;
  }, [body, sources, choices]);

  const handleCopy = useCallback(() => {
    const text = buildCopy(body, sources, choices);
    void navigator.clipboard.writeText(text);
    toast.success('Copied');
    onClose();
  }, [body, sources, choices, onClose]);

  const currentScopeKey = scopeKey(choices.scope);

  return ReactDOM.createPortal(
    <div
      ref={overlayRef}
      className="malpedia-copy-special-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Copy with options"
    >
      <div className="malpedia-copy-special-modal">
        <div className="malpedia-copy-special-header">
          <span>Copy Special</span>
          <button
            className="malpedia-copy-special-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="malpedia-copy-special-body">
          {/* Scope */}
          <div className="malpedia-copy-special-section">
            <label className="malpedia-copy-special-label">What to copy</label>
            <div className="malpedia-copy-special-radios">
              <label className="malpedia-copy-special-radio">
                <input
                  type="radio"
                  name="copy-scope"
                  checked={currentScopeKey === 'full'}
                  onChange={() => setScope({ kind: 'full' })}
                />
                Full message
              </label>
              {tables.map((t, i) => (
                <label key={`table-${i}`} className="malpedia-copy-special-radio">
                  <input
                    type="radio"
                    name="copy-scope"
                    checked={currentScopeKey === `table-${i}`}
                    onChange={() => setScope({ kind: 'table', index: i })}
                  />
                  Table {i + 1}
                  <span className="malpedia-copy-special-radio-hint">
                    {snippetFor(t)}
                  </span>
                </label>
              ))}
              <label
                className={`malpedia-copy-special-radio${!hasSources ? ' disabled' : ''}`}
              >
                <input
                  type="radio"
                  name="copy-scope"
                  disabled={!hasSources}
                  checked={currentScopeKey === 'sources'}
                  onChange={() => setScope({ kind: 'sources' })}
                />
                Sources list only
                {!hasSources && (
                  <span className="malpedia-copy-special-radio-hint">no sources</span>
                )}
              </label>
              <label
                className={`malpedia-copy-special-radio${!hasSelection ? ' disabled' : ''}`}
              >
                <input
                  type="radio"
                  name="copy-scope"
                  disabled={!hasSelection}
                  checked={currentScopeKey === 'selection'}
                  onChange={() =>
                    setScope({ kind: 'selection', text: selectionText })
                  }
                />
                Selected text
                {hasSelection && (
                  <span className="malpedia-copy-special-radio-hint">
                    {selectionText.length} chars
                  </span>
                )}
              </label>
            </div>
          </div>

          {/* References */}
          <div className="malpedia-copy-special-section">
            <label className="malpedia-copy-special-label">Exchange references</label>
            <select
              className="malpedia-copy-special-select"
              value={choices.refFormat}
              onChange={(e) =>
                update({ refFormat: e.target.value as CopyRefFormat })
              }
            >
              {REF_FORMAT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label} — {o.hint}
                </option>
              ))}
            </select>
          </div>

          {/* Format */}
          <div className="malpedia-copy-special-section">
            <label className="malpedia-copy-special-label">Format</label>
            <div className="malpedia-copy-special-pills">
              {FORMAT_PILLS.map((p) => {
                const disabled =
                  p.enabledFor === 'table' && choices.scope.kind !== 'table';
                return (
                  <button
                    key={p.value}
                    type="button"
                    disabled={disabled}
                    className={`malpedia-copy-special-pill${choices.format === p.value ? ' active' : ''}${disabled ? ' disabled' : ''}`}
                    onClick={() => update({ format: p.value })}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Include sources table — only meaningful for full message */}
          <div className="malpedia-copy-special-section">
            <label
              className={`malpedia-copy-special-checkbox${choices.scope.kind !== 'full' ? ' disabled' : ''}`}
            >
              <input
                type="checkbox"
                disabled={choices.scope.kind !== 'full' || !hasSources}
                checked={choices.includeSourceTable}
                onChange={(e) =>
                  update({ includeSourceTable: e.target.checked })
                }
              />
              Append sources table
            </label>
          </div>

          {/* Preview */}
          <div className="malpedia-copy-special-section">
            <label className="malpedia-copy-special-label">Preview</label>
            <pre className="malpedia-copy-special-preview">{preview || '(empty)'}</pre>
          </div>
        </div>

        <div className="malpedia-copy-special-actions">
          <button
            className="malpedia-copy-special-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="malpedia-copy-special-confirm"
            onClick={handleCopy}
          >
            Copy
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
